import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Card } from './ui/card';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { ArrowLeft, Plus, Loader2, Pencil, Trash2, Package, MoreVertical, Printer } from 'lucide-react';
import { format } from 'date-fns';
import { AppBar } from './ui/app-bar';
import { useIsAndroid } from '../hooks/use-android';
import { useBackNavigation } from '../hooks/use-back-navigation';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Label } from './ui/label';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from './ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { useToast } from './ui/use-toast';
import { useNavigate } from 'react-router-dom';
import SupplierPayments from './SupplierPayments';
import { getBaseUrl } from '@/lib/api';
import { getLocalDateString } from '@/lib/utils';
import { useSettings } from '../hooks/useSettings';
import DebitNotePreview, { type DebitNotePreviewData } from './DebitNotePreview';

interface SupplierInvoice {
  InvDateGen: string;
  InvRefNo: string;
  GRNNo: string;
  PayableAmount: number;
  SGST: number;
  CGST: number;
  IGST: number;
  CESS: number;
  Type: string;
  CompanyID?: number;
  LocationID?: number;
  CompanyName?: string;
  Location?: string;
  DueBalAmount?: number;
  ID?: number;
}

interface SupplierInvoicesPaymentsProps {
  token: string;
  supplierIds: number[];
  supplierState?: string;
  onBack: () => void;
}

type ActiveTab = 'invoices' | 'payments';

function formatCurrency(val?: number) {
  if (typeof val !== 'number') return '-';
  return `₹${val.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatCess(val?: number | null) {
  // If CESS is null or undefined, return 0.00
  if (val === null || val === undefined) {
    return '₹0.00';
  }
  return `₹${val.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatType(type?: string | number) {
  // Convert to string and check
  const typeStr = String(type);
  if (typeStr === '0') return 'Invoice';
  if (typeStr === '1') return 'Debit Note';
  return type || '-';
}

function formatDateDMY(dateStr?: string) {
  if (!dateStr) return '-';
  try {
    return format(new Date(dateStr), 'dd-MM-yyyy');
  } catch {
    return dateStr;
  }
}

const SupplierInvoicesPayments: React.FC<SupplierInvoicesPaymentsProps> = ({ 
  token, 
  supplierIds, 
  supplierState,
  onBack 
}) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<ActiveTab>('invoices');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedInvoices, setSelectedInvoices] = useState<Set<string>>(new Set());
  const [isAddInvoiceOpen, setIsAddInvoiceOpen] = useState(false);
  const [isUpdateInvoiceOpen, setIsUpdateInvoiceOpen] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [invoiceForm, setInvoiceForm] = useState({
    date: getLocalDateString(), // Current date (local timezone)
    invNo: '',
    companyId: '',
    locationId: '',
    grnNo: '',
    type: '0', // Default to Invoice (0 for Invoice, 1 for Debit Note)
    payableAmount: '0',
    sgst: '0',
    cgst: '0',
    igst: '0',
    cess: '0',
  });
  const [updateForm, setUpdateForm] = useState({
    id: 0,
    date: '',
    invNo: '',
  });
  const [isDebitNotePreviewOpen, setIsDebitNotePreviewOpen] = useState(false);
  const [debitNotePreviewData, setDebitNotePreviewData] = useState<DebitNotePreviewData | null>(null);
  const [isLoadingDebitNote, setIsLoadingDebitNote] = useState(false);
  const [debitNoteError, setDebitNoteError] = useState<string | null>(null);
  const isAndroid = useIsAndroid();
  const { primaryCompanyId } = useSettings();

  // Android back: close form/modal first, else payments->invoices, else invoices->supplier list
  useBackNavigation({
    onBack: () => {
      if (isAddInvoiceOpen) {
        setIsAddInvoiceOpen(false);
        resetInvoiceForm();
        return;
      }
      if (isUpdateInvoiceOpen) {
        setUpdateForm({ id: 0, date: '', invNo: '' });
        setIsUpdateInvoiceOpen(false);
        return;
      }
      if (isDeleteConfirmOpen) {
        setIsDeleteConfirmOpen(false);
        return;
      }
      if (isDebitNotePreviewOpen) {
        setIsDebitNotePreviewOpen(false);
        return;
      }
      if (activeTab === 'payments') {
        setActiveTab('invoices');
      } else {
        onBack();
      }
    },
    canGoBack: true,
    showExitConfirmation: false,
  });

  const baseUrl = getBaseUrl();
  const authToken = token.startsWith('Bearer ') ? token : `Bearer ${token}`;

  // For now, we'll use the first selected supplier ID
  // In the future, this could be enhanced to handle multiple suppliers
  const supplierId = supplierIds[0];

  const { data, isLoading, error } = useQuery({
    queryKey: ['supplier-invoices', supplierId, baseUrl],
    queryFn: async () => {
      console.log('=== Fetching Supplier Invoices ===');
      console.log('SupplierID:', supplierId);
      console.log('URL:', `${baseUrl}/api/SupplierInvoicesdetails?SupplierID=${supplierId}`);
      
      const response = await fetch(
        `${baseUrl}/api/SupplierInvoicesdetails?SupplierID=${supplierId}`,
        {
          headers: { 
            Authorization: authToken, 
            'Content-Type': 'application/json' 
          },
        }
      );
      if (!response.ok) throw new Error('Failed to fetch supplier invoices');
      const jsonData = await response.json();
      
      // Console log the received data in clean JSON format like Postman
      console.log('=== Supplier Invoices API Response ===');
      console.log('SupplierID:', supplierId);
      console.log(JSON.stringify(jsonData, null, 2));
      console.log('=== End of API Response ===');
      
      return jsonData;
    },
    enabled: !!supplierId && activeTab === 'invoices',
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
  });

  // Fetch supplier balance data
  const { data: balanceData, isLoading: isBalanceLoading, error: balanceError } = useQuery({
    queryKey: ['supplier-balance', supplierId, baseUrl],
    queryFn: async () => {
      console.log('=== Fetching Supplier Balance ===');
      console.log('SupplierID:', supplierId);
      console.log('URL:', `${baseUrl}/api/GetSupplierBalance?supplierId=${supplierId}`);
      
      const response = await fetch(
        `${baseUrl}/api/GetSupplierBalance?supplierId=${supplierId}`,
        {
          headers: { 
            Authorization: authToken, 
            'Content-Type': 'application/json' 
          },
        }
      );
      if (!response.ok) throw new Error('Failed to fetch supplier balance');
      const jsonData = await response.json();
      
      // Console log the received data in clean JSON format like Postman
      console.log('=== Supplier Balance API Response ===');
      console.log('SupplierID:', supplierId);
      console.log(JSON.stringify(jsonData, null, 2));
      console.log('=== End of API Response ===');
      
      return jsonData;
    },
    enabled: !!supplierId,
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
  });

  const invoices: SupplierInvoice[] = (data as any)?.data || [];
  const dueBalAmount = (data as any)?.data?.[0]?.DueBalAmount || 0;
  const supplierIdFromDetails = (data as any)?.data?.[0]?.SupplierID ?? supplierId;
  const supplierName = (data as any)?.data?.[0]?.SupplierName || 'Supplier';

  // Extract balance data from API response
  console.log('=== Balance Data Extraction Debug ===');
  console.log('Raw balanceData:', balanceData);
  console.log('balanceData type:', typeof balanceData);
  console.log('balanceData keys:', balanceData ? Object.keys(balanceData) : 'null/undefined');
  
  // Try different possible structures for the API response
  let totalPayableAmount = 0;
  let totalPaidAmount = 0;
  let dueAmount = 0;

  if (balanceData) {
    // Check if data is nested under a 'data' property
    if (balanceData.data) {
      console.log('Found nested data structure');
      console.log('balanceData.data:', balanceData.data);
      totalPayableAmount = balanceData.data.TotalPayableAmount || 0;
      totalPaidAmount = balanceData.data.TotalPaidAmount || 0;
      dueAmount = balanceData.data.DueAmount || 0;
    } else {
      // Try direct access
      console.log('Using direct access to balanceData');
      totalPayableAmount = balanceData.TotalPayableAmount || 0;
      totalPaidAmount = balanceData.TotalPaidAmount || 0;
      dueAmount = balanceData.DueAmount || 0;
    }
  }

  console.log('Extracted values:');
  console.log('- TotalPayableAmount:', totalPayableAmount);
  console.log('- TotalPaidAmount:', totalPaidAmount);
  console.log('- DueAmount:', dueAmount);
  console.log('=== End Balance Data Extraction Debug ===');

  const { data: companiesData, isLoading: isCompaniesLoading } = useQuery({
    queryKey: ['companies'],
    queryFn: async () => {
      const response = await fetch(`${baseUrl}/api/Companies`, {
        headers: {
          Authorization: authToken,
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) throw new Error('Failed to fetch companies');
      const jsonData = await response.json();
      
      // Console log the received data in clean JSON format
      console.log('=== Companies API Response ===');
      console.log(JSON.stringify(jsonData, null, 2));
      console.log('=== End of Companies API Response ===');
      
      return jsonData;
    },
    enabled: isAddInvoiceOpen,
    staleTime: 1000 * 60 * 5,
  });

  const { data: locationsData, isLoading: isLocationsLoading } = useQuery({
    queryKey: ['locations'],
    queryFn: async () => {
      const response = await fetch(`${baseUrl}/api/Locations`, {
        headers: {
          Authorization: authToken,
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) throw new Error('Failed to fetch locations');
      const jsonData = await response.json();
      
      // Console log the received data in clean JSON format
      console.log('=== Locations API Response ===');
      console.log(JSON.stringify(jsonData, null, 2));
      console.log('=== End of Locations API Response ===');
      
      return jsonData;
    },
    enabled: isAddInvoiceOpen,
    staleTime: 1000 * 60 * 5,
  });

  const companiesSource = Array.isArray(companiesData?.data) ? companiesData?.data : Array.isArray(companiesData) ? companiesData : [];
  const locationsSource = Array.isArray(locationsData?.data) ? locationsData?.data : Array.isArray(locationsData) ? locationsData : [];

  const companies = Array.isArray(companiesSource) ? companiesSource : [];
  const locations = Array.isArray(locationsSource) ? locationsSource : [];

  // Set default company from Settings (Primary Company ID) when data is loaded
  React.useEffect(() => {
    if (companies.length > 0 && !invoiceForm.companyId) {
      const defaultCompany = primaryCompanyId
        ? companies.find((c: any) => String(c.Id) === primaryCompanyId || String(c.CompanyID) === primaryCompanyId)
        : null;
      const companyToUse = defaultCompany || companies[0];
      const companyId = String(companyToUse.Id || companyToUse.CompanyID);
      setInvoiceForm(prev => ({ ...prev, companyId }));
    }
  }, [companies, invoiceForm.companyId, primaryCompanyId]);

  React.useEffect(() => {
    if (locations.length > 0 && !invoiceForm.locationId) {
      const firstLocation = locations[0];
      const locationId = String(firstLocation.ID || firstLocation.LocationID || firstLocation.LocationId);
      setInvoiceForm(prev => ({ ...prev, locationId }));
    }
  }, [locations, invoiceForm.locationId]);

  // Debug: Log when supplierId changes
  React.useEffect(() => {
    console.log('\n🏢 ═══════════════════════════════════════════════════════════');
    console.log('🏢 SUPPLIER INVOICES & PAYMENTS COMPONENT');
    console.log('🏢 ═══════════════════════════════════════════════════════════');
    console.log('📌 Supplier Information:');
    console.log('   ├─ Current Supplier ID:', supplierId);
    console.log('   ├─ All Supplier IDs:', supplierIds);
    console.log('   ├─ Active Tab:', activeTab);
    console.log('   └─ Base URL:', baseUrl);
    console.log('📌 Component Props:');
    console.log('   ├─ Token present:', !!token);
    console.log('   ├─ Supplier State:', supplierState);
    console.log('   └─ OnBack function present:', typeof onBack);
    console.log('🏢 ═══════════════════════════════════════════════════════════\n');
  }, [supplierId, supplierIds, activeTab, baseUrl, token, supplierState, onBack]);

  // Debug: Log when supplierIds change
  React.useEffect(() => {
    console.log('=== SupplierIds Change Debug ===');
    console.log('New supplierIds:', supplierIds);
    console.log('Previous supplierId:', supplierId);
    console.log('Will trigger balance API call for supplierId:', supplierIds[0]);
    console.log('=== End SupplierIds Change Debug ===');
  }, [supplierIds]);

  // Debug: Log balance data changes separately
  React.useEffect(() => {
    console.log('📌 Balance API Debug:');
    console.log('   ├─ Balance Data:', balanceData);
    console.log('   ├─ Is Loading:', isBalanceLoading);
    console.log('   ├─ Error:', balanceError);
  }, [balanceData, isBalanceLoading, balanceError]);

  // Debug: Log when switching tabs
  React.useEffect(() => {
    if (activeTab === 'payments') {
      console.log('\n💰 ═══════════════════════════════════════════════════════════');
      console.log('💰 PAYMENTS TAB ACTIVATED');
      console.log('💰 ═══════════════════════════════════════════════════════════');
      console.log('📌 Tab Information:');
      console.log('   ├─ Active Tab: PAYMENTS');
      console.log('   ├─ Supplier ID to be passed:', supplierId);
      console.log('   ├─ Token present:', !!token);
      console.log('   └─ SupplierPayments component will receive Supplier ID:', supplierId);
      console.log('💰 ═══════════════════════════════════════════════════════════\n');
    }
  }, [activeTab, supplierId, token]);

  // Filter invoices based on search query (coerce to string in case API returns number etc.)
  const filteredInvoices = invoices.filter(invoice =>
    String(invoice.InvRefNo ?? '').toLowerCase().includes(searchQuery.toLowerCase()) ||
    String(invoice.GRNNo ?? '').toLowerCase().includes(searchQuery.toLowerCase()) ||
    String(invoice.Type ?? '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Reverse the order to show newest first
  const displayedInvoices = [...filteredInvoices].reverse();

  // Handle single selection (one invoice at a time)
  const handleCheckboxChange = (invoiceRef: string) => {
    setSelectedInvoices(prev => {
      if (prev.has(invoiceRef)) {
        return new Set<string>();
      }
      return new Set([invoiceRef]);
    });
  };

  // Switch back to Invoices tab if all invoices are unchecked while on Payments tab
  React.useEffect(() => {
    if (activeTab === 'payments' && selectedInvoices.size === 0) {
      setActiveTab('invoices');
    }
  }, [selectedInvoices.size, activeTab]);

  const resetInvoiceForm = () => {
    const today = getLocalDateString();
    setInvoiceForm({
      date: today,
      invNo: '',
      companyId: '',
      locationId: '',
      grnNo: '',
      type: '0', // Default to Invoice (0 for Invoice, 1 for Debit Note)
      payableAmount: '0',
      sgst: '0',
      cgst: '0',
      igst: '0',
      cess: '0',
    });
  };

  const handleOpenAddInvoice = () => {
    resetInvoiceForm();
    setIsAddInvoiceOpen(true);
  };

  const handleCloseAddInvoice = () => {
    resetInvoiceForm();
    setIsAddInvoiceOpen(false);
  };

  const handleOpenUpdateInvoice = () => {
    // Get the first (and should be only) selected invoice
      const selectedInvoiceRef = Array.from(selectedInvoices)[0];
      const selectedInvoice = displayedInvoices.find(inv => inv.InvRefNo === selectedInvoiceRef);
    
    if (selectedInvoice) {
      // Parse the date from DD-MM-YYYY to YYYY-MM-DD
      let formattedDate = '';
      if (selectedInvoice.InvDateGen) {
        try {
          const dateObj = new Date(selectedInvoice.InvDateGen);
          formattedDate = format(dateObj, 'yyyy-MM-dd');
        } catch {
          formattedDate = '';
        }
      }
      
      setUpdateForm({
        id: selectedInvoice.ID || 0,
        date: formattedDate,
        invNo: selectedInvoice.InvRefNo || '',
      });
      setIsUpdateInvoiceOpen(true);
    }
  };

  const handleCloseUpdateInvoice = () => {
    setUpdateForm({
      id: 0,
      date: '',
      invNo: '',
    });
    setIsUpdateInvoiceOpen(false);
  };

  const handleUpdateInvoice = async () => {
    // Validate required fields
    const missingFields = [];
    if (!updateForm.date) missingFields.push('Date');
    if (!updateForm.invNo?.trim()) missingFields.push('Invoice Number');

    if (missingFields.length > 0) {
      toast({
        variant: 'destructive',
        title: 'Missing Required Fields',
        description: `Please fill in the following fields: ${missingFields.join(', ')}`,
      });
      return;
    }

    try {
      setIsSubmitting(true);
      const payload = {
        ID: updateForm.id,
        InvDate: updateForm.date,
        InvNo: updateForm.invNo.trim(),
      };

      console.log('=== PUT api/UpdateSupplierInvoice Request ===');
      console.log('URL:', `${baseUrl}/api/UpdateSupplierInvoice`);
      console.log('Method: PUT');
      console.log('Request Body:');
      console.log(JSON.stringify(payload, null, 2));
      console.log('=== End of Request ===');

      const response = await fetch(`${baseUrl}/api/UpdateSupplierInvoice`, {
        method: 'PUT',
        headers: {
          Authorization: authToken,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      console.log('=== PUT api/UpdateSupplierInvoice Response ===');
      console.log('Status:', response.status, response.statusText);
      console.log('Response Headers:', Object.fromEntries(response.headers.entries()));

      if (!response.ok) {
        const errorText = await response.text();
        console.log('Error Response Body:', errorText);
        console.log('=== End of Response ===');
        
        toast({
          variant: 'destructive',
          title: 'Update failed',
          description: errorText || 'Failed to update supplier invoice',
        });
        return;
      }

      const responseData = await response.json();
      console.log('Response Body:');
      console.log(JSON.stringify(responseData, null, 2));
      console.log('=== End of Response ===');

      // Check if API returned an error status (status = -1 means failure)
      if (responseData.status === -1) {
        const errorMessage = responseData.message || 'Failed to update supplier invoice';
        toast({
          variant: 'destructive',
          title: 'Update failed',
          description: errorMessage,
        });
        return;
      }

      // Check if the response indicates failure
      if (responseData.success === false) {
        const errorMessage = responseData.message || 'Failed to update supplier invoice';
        toast({
          variant: 'destructive',
          title: 'Update failed',
          description: errorMessage,
        });
        return;
      }

      // Success (status = 0 means success)
      toast({
        title: 'Invoice updated',
        description: 'The supplier invoice has been updated successfully.',
      });

      await queryClient.invalidateQueries({ queryKey: ['supplier-invoices', supplierId] });
      await queryClient.invalidateQueries({ queryKey: ['supplier-balance'] });
      setSelectedInvoices(new Set()); // Clear selection
      handleCloseUpdateInvoice();
    } catch (err) {
      console.error('Error updating invoice:', err);
      toast({
        variant: 'destructive',
        title: 'Update failed',
        description: err instanceof Error ? err.message : 'An unexpected error occurred while updating the invoice.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSaveInvoice = async () => {
    // Validate all required fields
    const missingFields = [];
    if (!invoiceForm.date) missingFields.push('Date');
    if (!invoiceForm.invNo?.trim()) missingFields.push('Invoice Number');
    if (!invoiceForm.companyId) missingFields.push('Company');
    if (!invoiceForm.locationId) missingFields.push('Location');

    if (missingFields.length > 0) {
      toast({
        variant: 'destructive',
        title: 'Missing Required Fields',
        description: `Please fill in the following fields: ${missingFields.join(', ')}`,
      });
      return;
    }

    if (!supplierIdFromDetails) {
      toast({
        variant: 'destructive',
        title: 'Supplier not found',
        description: 'Unable to determine supplier ID. Please try again.',
      });
      return;
    }

    try {
      setIsSubmitting(true);
      const payload: any = {
        SupplierID: String(supplierIdFromDetails),
        InvDate: invoiceForm.date,
        InvNo: invoiceForm.invNo.trim(),
        CompanyID: Number(invoiceForm.companyId),
        LocationID: Number(invoiceForm.locationId),
        Type: invoiceForm.type ? Number(invoiceForm.type) : 1, // Default to 1 if not selected
        PayableAmount: parseFloat(invoiceForm.payableAmount) || 0,
        SGST: parseFloat(invoiceForm.sgst) || 0,
        CGST: parseFloat(invoiceForm.cgst) || 0,
        IGST: parseFloat(invoiceForm.igst) || 0,
        CESS: parseFloat(invoiceForm.cess) || 0,
      };

      // Add GrnNo only if it's not empty (optional field)
      if (invoiceForm.grnNo.trim()) {
        payload.GrnNo = invoiceForm.grnNo.trim();
      }

      // Console log the request payload in clean JSON format like Postman
      console.log('=== POST api/SupplierInvoices Request ===');
      console.log('URL:', `${baseUrl}/api/SupplierInvoices`);
      console.log('Method: POST');
      console.log('Headers:', {
        Authorization: authToken.substring(0, 20) + '...',
        'Content-Type': 'application/json',
      });
      console.log('Request Body:');
      console.log(JSON.stringify(payload, null, 2));
      console.log('=== End of Request ===');

      const response = await fetch(`${baseUrl}/api/SupplierInvoices`, {
        method: 'POST',
        headers: {
          Authorization: authToken,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      // Console log the response
      console.log('=== POST api/SupplierInvoices Response ===');
      console.log('Status:', response.status, response.statusText);
      console.log('Response Headers:', Object.fromEntries(response.headers.entries()));

      if (!response.ok) {
        const errorText = await response.text();
        console.log('Error Response Body:', errorText);
        console.log('=== End of Response ===');
        throw new Error(errorText || 'Failed to save supplier invoice');
      }

      const responseData = await response.json();
      console.log('Response Body:');
      console.log(JSON.stringify(responseData, null, 2));
      console.log('=== End of Response ===');

      // Check if API returned status -1 (duplicate invoice)
      if (responseData.status === -1) {
        toast({
          variant: 'destructive',
          title: 'Duplicate Invoice',
          description: 'Invoice with the same number already exists.',
        });
        return;
      }

      toast({
        title: 'Invoice added',
        description: 'The supplier invoice has been saved successfully.',
      });

      await queryClient.invalidateQueries({ queryKey: ['supplier-invoices', supplierId] });
      await queryClient.invalidateQueries({ queryKey: ['supplier-balance'] });
      handleCloseAddInvoice();
    } catch (err) {
      console.error(err);
      toast({
        variant: 'destructive',
        title: 'Save failed',
        description: err instanceof Error ? err.message : 'An unexpected error occurred while saving the invoice.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenDeleteConfirm = () => {
    setIsDeleteConfirmOpen(true);
  };

  const handleCloseDeleteConfirm = () => {
    setIsDeleteConfirmOpen(false);
  };

  const handleDeleteInvoice = async () => {
    if (selectedInvoices.size === 0) {
      toast({
        variant: 'destructive',
        title: 'No Invoice Selected',
        description: 'Please select at least one invoice to delete.',
      });
      return;
    }

    try {
      setIsDeleting(true);
      
      // Get IDs of all selected invoices
      const selectedInvoiceRefs = Array.from(selectedInvoices);
      const selectedInvoiceObjects = displayedInvoices.filter(inv => 
        selectedInvoiceRefs.includes(inv.InvRefNo)
      );

      // Track successes and failures
      let successCount = 0;
      let failureCount = 0;
      const errors: string[] = [];

      // Delete each selected invoice
      for (const invoice of selectedInvoiceObjects) {
        if (!invoice.ID) {
          console.error('Invoice ID not found for:', invoice.InvRefNo);
          failureCount++;
          errors.push(`Invoice ${invoice.InvRefNo}: ID not found`);
          continue;
        }

        const payload = {
          ID: invoice.ID,
        };

        console.log('\n=== DELETE api/deleteSupplierInvoice Request ===');
        console.log(`DELETE ${baseUrl}/api/deleteSupplierInvoice`);
        console.log('Headers:', {
          'Authorization': authToken.substring(0, 20) + '...',
          'Content-Type': 'application/json',
        });
        console.log('Request Body:');
        console.log(JSON.stringify(payload, null, 2));
        console.log('==========================================\n');

        const response = await fetch(`${baseUrl}/api/deleteSupplierInvoice`, {
          method: 'DELETE',
          headers: {
            Authorization: authToken,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        });

        console.log('=== DELETE api/deleteSupplierInvoice Response ===');
        console.log('Status:', response.status, response.statusText);
        console.log('Response Headers:', Object.fromEntries(response.headers.entries()));

        if (!response.ok) {
          const errorText = await response.text();
          console.log('Response Body (Error):', errorText);
          console.log('==========================================\n');
          
          failureCount++;
          errors.push(`Invoice ${invoice.InvRefNo}: ${errorText || 'HTTP error'}`);
          continue;
        }

        const responseData = await response.json();
        console.log('Response Body:');
        console.log(JSON.stringify(responseData, null, 2));
        console.log('==========================================\n');

        // Check if API returned an error status (status = -1 means failure)
        if (responseData.status === -1) {
          const errorMessage = responseData.message || 'Unknown error';
          console.error(`❌ Failed to delete invoice ${invoice.InvRefNo}: ${errorMessage}`);
          failureCount++;
          errors.push(`Invoice ${invoice.InvRefNo}: ${errorMessage}`);
          continue;
        }

        // Check if status = 0 (success)
        if (responseData.status === 0) {
          console.log(`✅ Successfully deleted invoice ${invoice.InvRefNo}`);
          successCount++;
        } else {
          // Unexpected status
          console.warn(`⚠️ Unexpected status ${responseData.status} for invoice ${invoice.InvRefNo}`);
          failureCount++;
          errors.push(`Invoice ${invoice.InvRefNo}: Unexpected status ${responseData.status}`);
        }
      }

      // Show appropriate message based on results
      if (failureCount > 0 && successCount === 0) {
        // All failed
        toast({
          variant: 'destructive',
          title: 'Delete Failed',
          description: errors.length === 1 ? errors[0] : `Failed to delete ${failureCount} invoice(s). Check console for details.`,
        });
      } else if (failureCount > 0 && successCount > 0) {
        // Partial success
        toast({
          title: 'Partially Successful',
          description: `Deleted ${successCount} invoice(s), but ${failureCount} failed. Check console for details.`,
        });
      } else if (successCount > 0) {
        // All succeeded
        toast({
          title: 'Success',
          description: successCount === 1 
            ? 'Invoice deleted successfully.' 
            : `${successCount} invoices deleted successfully.`,
        });
      }

      // Refresh the invoice list and balance if at least one was deleted
      if (successCount > 0) {
        await queryClient.invalidateQueries({ queryKey: ['supplier-invoices', supplierId] });
        await queryClient.invalidateQueries({ queryKey: ['supplier-balance'] });
      }
      
      // Clear selection and close dialog
      setSelectedInvoices(new Set());
      handleCloseDeleteConfirm();
    } catch (err) {
      console.error('❌ Error deleting invoice:', err);
      toast({
        variant: 'destructive',
        title: 'Delete Failed',
        description: err instanceof Error ? err.message : 'An unexpected error occurred while deleting the invoice.',
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const isUpdateInvoiceEnabled = selectedInvoices.size === 1; // Only enable when exactly one invoice is selected
  const isDeleteInvoiceEnabled = selectedInvoices.size > 0; // Enable when at least one invoice is selected
  const isPaymentsTabEnabled = selectedInvoices.size > 0; // Enable Payments tab when at least one invoice is selected
  const isAddEditQuantitiesEnabled = selectedInvoices.size === 1; // Only enable when exactly one invoice is selected

  // Enable "Print Debit note" only when at least one selected row is a Debit Note (Type === '1')
  const selectedInvoiceRefs = Array.from(selectedInvoices);
  const selectedInvoiceObjects = displayedInvoices.filter(inv => selectedInvoiceRefs.includes(inv.InvRefNo));
  const hasSelectedDebitNote = selectedInvoiceObjects.some(inv => String(inv.Type) === '1');
  const isPrintDebitNoteEnabled = hasSelectedDebitNote;

  // Handle Add/Edit Quantities navigation
  const handleAddEditQuantities = () => {
    if (selectedInvoices.size !== 1) {
      toast({
        variant: 'destructive',
        title: 'No Invoice Selected',
        description: 'Please select exactly one invoice to add/edit quantities.',
      });
      return;
    }

      const selectedInvoiceRef = Array.from(selectedInvoices)[0];
      const selectedInvoice = displayedInvoices.find(inv => inv.InvRefNo === selectedInvoiceRef);
    
    if (!selectedInvoice) {
      toast({
        variant: 'destructive',
        title: 'Invoice Not Found',
        description: 'Selected invoice not found.',
      });
      return;
    }

    console.log('\n🚀 ═══════════════════════════════════════════════════════════');
    console.log('🚀 NAVIGATING TO PRODUCT ENTRY WITH LOCATION');
    console.log('🚀 ═══════════════════════════════════════════════════════════');
    console.log('📋 Selected Invoice Details:');
    console.log('   ├─ Invoice ID:', selectedInvoice.ID);
    console.log('   ├─ Invoice Ref No:', selectedInvoice.InvRefNo);
    console.log('   ├─ Supplier Name:', supplierName);
    console.log('   ├─ Location ID:', selectedInvoice.LocationID);
    console.log('   ├─ Location Name:', selectedInvoice.Location);
    console.log('   └─ Company ID:', selectedInvoice.CompanyID);
    console.log('🎯 Navigation State Being Passed:');
    console.log(JSON.stringify({
      invoiceId: selectedInvoice.ID,
      invoiceRefNo: selectedInvoice.InvRefNo,
      supplierName: supplierName,
      locationId: selectedInvoice.LocationID,
      supplierState: supplierState,
    }, null, 2));
    console.log('🚀 Navigation Target: /supplier-invoice-product-entry');
    console.log('🚀 ═══════════════════════════════════════════════════════════\n');

    navigate('/supplier-invoice-product-entry', {
      state: {
        invoiceId: selectedInvoice.ID,
        invoiceRefNo: selectedInvoice.InvRefNo,
        supplierName: supplierName,
        supplierId: supplierIdFromDetails || supplierId, // Add supplier ID
        locationId: selectedInvoice.LocationID,
        supplierState: supplierState,
        fromInvoicesPayments: true, // Add flag to indicate source
        type: selectedInvoice.Type, // Add Type information
        // Add supplier context for back navigation
        backToSupplierIds: supplierIds,
        backToSupplierState: supplierState,
      },
      replace: false, // Don't replace history to allow back navigation
    });
  };

  // Helper functions for individual invoice actions
  const handleEditInvoice = (invoice: SupplierInvoice) => {
    // Select the invoice and open update dialog
    setSelectedInvoices(new Set([invoice.InvRefNo]));
    
    // Parse the date from DD-MM-YYYY to YYYY-MM-DD
    let formattedDate = '';
    if (invoice.InvDateGen) {
      try {
        const dateObj = new Date(invoice.InvDateGen);
        formattedDate = format(dateObj, 'yyyy-MM-dd');
      } catch {
        formattedDate = '';
      }
    }
    
    setUpdateForm({
      id: invoice.ID || 0,
      date: formattedDate,
      invNo: invoice.InvRefNo || '',
    });
    setIsUpdateInvoiceOpen(true);
  };

  const handleDeleteInvoiceFromCard = (invoice: SupplierInvoice) => {
    // Select the invoice and open delete confirmation
    setSelectedInvoices(new Set([invoice.InvRefNo]));
    setIsDeleteConfirmOpen(true);
  };

  const handleAddQuantities = (invoice: SupplierInvoice) => {
    // Select the invoice and navigate to product entry
    setSelectedInvoices(new Set([invoice.InvRefNo]));
    handleAddEditQuantities();
  };

  const handlePrintDebitNote = async () => {
    const selectedRef = Array.from(selectedInvoices)[0];
    const selected = displayedInvoices.find((inv) => inv.InvRefNo === selectedRef);
    if (!selected || String(selected.Type) !== '1') {
      toast({
        variant: 'destructive',
        title: 'Invalid selection',
        description: 'Please select a Debit Note to print.',
      });
      return;
    }
    const supplierInvoicesId = selected.ID;
    if (supplierInvoicesId == null || supplierInvoicesId === undefined) {
      toast({
        variant: 'destructive',
        title: 'Cannot print',
        description: 'Debit note ID is missing.',
      });
      return;
    }
    setDebitNoteError(null);
    setDebitNotePreviewData(null);
    setIsDebitNotePreviewOpen(true);
    setIsLoadingDebitNote(true);
    try {
      const url = `${baseUrl}/api/GetSupplierDebit?SupplierInvoicesID=${supplierInvoicesId}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          Authorization: authToken,
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) {
        throw new Error(`Failed to load debit note: ${response.status}`);
      }
      const json = await response.json();
      if (Array.isArray(json)) {
        setDebitNoteError(json.length === 0 ? 'No debit note data found.' : 'Invalid response.');
        setDebitNotePreviewData(null);
        return;
      }
      if (json.status !== 0 || !json.Company || !json.Supplier) {
        setDebitNoteError(json?.message || 'No debit note data found.');
        setDebitNotePreviewData(null);
        return;
      }
      setDebitNotePreviewData({
        Company: json.Company,
        Supplier: json.Supplier,
        Items: Array.isArray(json.Items) ? json.Items : [],
        TotalRecords: json.TotalRecords,
      });
      setDebitNoteError(null);
    } catch (err) {
      setDebitNoteError(err instanceof Error ? err.message : 'Failed to load debit note.');
      setDebitNotePreviewData(null);
    } finally {
      setIsLoadingDebitNote(false);
    }
  };

  const handleCloseDebitNotePreview = () => {
    setIsDebitNotePreviewOpen(false);
    setDebitNotePreviewData(null);
    setDebitNoteError(null);
  };

  const isCompanyDisabled = isCompaniesLoading || companies.length === 0;
  const isLocationDisabled = isLocationsLoading || locations.length === 0;

  return (
    <div className="h-screen flex flex-col lg:space-y-4 lg:p-8">
      {/* Mobile App Bar */}
      <div className="lg:hidden">
        <AppBar 
          title="Invoices & Payments" 
          onBack={onBack}
          showBackButton={true}
        />
      </div>
      
      {/* Mobile content with top padding for app bar */}
      <div className="lg:hidden flex flex-col px-4 pb-4 pt-14">
        {/* Balance Cards - Mobile Optimized */}
        <div className="grid grid-cols-3 gap-1.5 mb-3">
          <Card className="bg-gradient-to-r from-blue-50 to-blue-100 border-blue-200 shadow-sm">
            <div className="p-2">
              <div className="flex flex-col items-center justify-center text-center">
                <div className="h-6 w-6 bg-blue-200 rounded-full flex items-center justify-center mb-1">
                  <span className="text-blue-600 font-semibold text-xs">₹</span>
                </div>
                <p className="text-xs font-medium text-blue-600 mb-0.5">Payable</p>
                {isBalanceLoading ? (
                  <div className="h-4 w-12 bg-blue-200 rounded animate-pulse"></div>
                ) : (
                  <p className="text-sm font-bold text-blue-900">
                    {formatCurrency(totalPayableAmount)}
                  </p>
                )}
              </div>
            </div>
          </Card>

          <Card className="bg-gradient-to-r from-green-50 to-green-100 border-green-200 shadow-sm">
            <div className="p-2">
              <div className="flex flex-col items-center justify-center text-center">
                <div className="h-6 w-6 bg-green-200 rounded-full flex items-center justify-center mb-1">
                  <span className="text-green-600 font-semibold text-xs">✓</span>
                </div>
                <p className="text-xs font-medium text-green-600 mb-0.5">Paid</p>
                {isBalanceLoading ? (
                  <div className="h-4 w-12 bg-green-200 rounded animate-pulse"></div>
                ) : (
                  <p className="text-sm font-bold text-green-900">
                    {formatCurrency(totalPaidAmount)}
                  </p>
                )}
              </div>
            </div>
          </Card>

          <Card className="bg-gradient-to-r from-orange-50 to-orange-100 border-orange-200 shadow-sm">
            <div className="p-2">
              <div className="flex flex-col items-center justify-center text-center">
                <div className="h-6 w-6 bg-orange-200 rounded-full flex items-center justify-center mb-1">
                  <span className="text-orange-600 font-semibold text-xs">!</span>
                </div>
                <p className="text-xs font-medium text-orange-600 mb-0.5">Due</p>
                {isBalanceLoading ? (
                  <div className="h-4 w-12 bg-orange-200 rounded animate-pulse"></div>
                ) : (
                  <p className="text-sm font-bold text-orange-900">
                    {formatCurrency(dueAmount)}
                  </p>
                )}
              </div>
            </div>
          </Card>
        </div>

        {/* Tab Buttons */}
        <div className="flex gap-2 mb-4">
          <Button
            variant={activeTab === 'invoices' ? 'default' : 'outline'}
            onClick={() => setActiveTab('invoices')}
            className="flex-1"
          >
            Invoices
          </Button>
          <Button
            variant={activeTab === 'payments' ? 'default' : 'outline'}
            onClick={() => setActiveTab('payments')}
            className="flex-1"
            disabled={!isPaymentsTabEnabled}
            title={!isPaymentsTabEnabled ? 'Please select at least one invoice to view payments' : ''}
          >
            Payments
          </Button>
        </div>

        {/* Search and Add - Mobile Optimized */}
        {activeTab === 'invoices' && (
          <div className="mb-4">
            {/* Search bar with inline Add button */}
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Input
                  placeholder="Search invoices..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  className="w-full pr-10"
                />
                {searchQuery && (
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    onClick={() => setSearchQuery('')}
                    aria-label="Clear search"
                    style={{ zIndex: 2 }}
                  >
                    ×
                  </button>
                )}
              </div>
              <Button
                variant="default"
                size="sm"
                className="flex-shrink-0 h-10 px-3"
                onClick={handleOpenAddInvoice}
                title="Add new invoice"
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Desktop header */}
      <div className="hidden lg:block space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Button
              onClick={onBack}
              variant="outline"
              className="flex items-center gap-2 px-3 py-2"
            >
              <ArrowLeft className="h-5 w-5" />
              <span>Back</span>
            </Button>
            <h1 className="text-lg font-bold ml-2">Invoices & Payments</h1>
          </div>
        </div>

        {/* Balance Cards - Desktop */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="bg-gradient-to-r from-blue-50 to-blue-100 border-blue-200 shadow-sm hover:shadow-md transition-shadow duration-200">
            <div className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-blue-600 mb-2">Total Payable Amount</p>
                  {isBalanceLoading ? (
                    <div className="h-8 w-32 bg-blue-200 rounded animate-pulse"></div>
                  ) : (
                    <p className="text-3xl font-bold text-blue-900">
                      {formatCurrency(totalPayableAmount)}
                    </p>
                  )}
                </div>
                <div className="h-12 w-12 bg-blue-200 rounded-full flex items-center justify-center">
                  <span className="text-blue-600 font-bold text-lg">₹</span>
                </div>
              </div>
            </div>
          </Card>

          <Card className="bg-gradient-to-r from-green-50 to-green-100 border-green-200 shadow-sm hover:shadow-md transition-shadow duration-200">
            <div className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-green-600 mb-2">Total Paid Amount</p>
                  {isBalanceLoading ? (
                    <div className="h-8 w-32 bg-green-200 rounded animate-pulse"></div>
                  ) : (
                    <p className="text-3xl font-bold text-green-900">
                      {formatCurrency(totalPaidAmount)}
                    </p>
                  )}
                </div>
                <div className="h-12 w-12 bg-green-200 rounded-full flex items-center justify-center">
                  <span className="text-green-600 font-bold text-lg">✓</span>
                </div>
              </div>
            </div>
          </Card>

          <Card className="bg-gradient-to-r from-orange-50 to-orange-100 border-orange-200 shadow-sm hover:shadow-md transition-shadow duration-200">
            <div className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-orange-600 mb-2">Due Amount</p>
                  {isBalanceLoading ? (
                    <div className="h-8 w-32 bg-orange-200 rounded animate-pulse"></div>
                  ) : (
                    <p className="text-3xl font-bold text-orange-900">
                      {formatCurrency(dueAmount)}
                    </p>
                  )}
                </div>
                <div className="h-12 w-12 bg-orange-200 rounded-full flex items-center justify-center">
                  <span className="text-orange-600 font-bold text-lg">!</span>
                </div>
              </div>
            </div>
          </Card>
        </div>

        {/* Tab Buttons */}
        <div className="flex gap-2">
          <Button
            variant={activeTab === 'invoices' ? 'default' : 'outline'}
            onClick={() => setActiveTab('invoices')}
            className="min-w-[120px]"
          >
            Invoices
          </Button>
          <Button
            variant={activeTab === 'payments' ? 'default' : 'outline'}
            onClick={() => setActiveTab('payments')}
            className="min-w-[120px]"
            disabled={!isPaymentsTabEnabled}
            title={!isPaymentsTabEnabled ? 'Please select at least one invoice to view payments' : ''}
          >
            Payments
          </Button>
        </div>

        {/* Search and Add */}
        {activeTab === 'invoices' && (
          <div className="flex items-center gap-2">
            <div className="relative max-w-sm flex-1">
              <Input
                placeholder="Search invoices..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="w-full pr-10"
              />
              {searchQuery && (
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  onClick={() => setSearchQuery('')}
                  aria-label="Clear search"
                  style={{ zIndex: 2 }}
                >
                  ×
                </button>
              )}
            </div>
            <Button
              variant={isUpdateInvoiceEnabled?"default":"outline"}
              onClick={handleOpenUpdateInvoice}
              disabled={!isUpdateInvoiceEnabled}
              title="Select one invoice to update"
              className={isUpdateInvoiceEnabled?"bg-purple-600 hover:bg-purple-700 text-white":""}
            >
              <Pencil className="h-5 w-5 mr-1" />
              <span>Update</span>
            </Button>
            <Button
              variant={isDeleteInvoiceEnabled ? "default":"outline" }
              onClick={handleOpenDeleteConfirm}
              disabled={!isDeleteInvoiceEnabled}
              title="Select at least one invoice to delete"
              className={isDeleteInvoiceEnabled ? "bg-red-600 hover:bg-red-700 text-white":""}
            >
              <Trash2 className="h-5 w-5 mr-1 text-white-600" />
              <span>Delete</span>
            </Button>
            <Button
              variant={isAddEditQuantitiesEnabled ? "default" : "outline"}
              onClick={handleAddEditQuantities}
              disabled={!isAddEditQuantitiesEnabled}
              title="Select one invoice to add/edit quantities"
              className={isAddEditQuantitiesEnabled ? "bg-green-600 hover:bg-green-700 text-white" : ""}
            >
              <Package className="h-5 w-5 mr-1" />
              <span>Add/Edit Quantities</span>
            </Button>
            <Button
              variant={isPrintDebitNoteEnabled ? "default" : "outline"}
              onClick={handlePrintDebitNote}
              disabled={!isPrintDebitNoteEnabled}
              title={isPrintDebitNoteEnabled ? "Print selected Debit note" : "Select a Debit Note to enable"}
              className={isPrintDebitNoteEnabled ? "bg-slate-700 hover:bg-slate-800 text-white" : ""}
            >
              <Printer className="h-5 w-5 mr-1" />
              <span>Print Debit note</span>
            </Button>
            <Button
              variant="default"
              onClick={handleOpenAddInvoice}
            >
              <Plus className="h-5 w-5 mr-1" />
              <span>Add Invoice</span>
            </Button>
          </div>
        )}
      </div>

      {/* Content Area */}
      {activeTab === 'invoices' ? (
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Desktop Table View */}
          <div className="hidden lg:block flex-1 overflow-auto rounded-md border">
            {isLoading ? (
              <div className="flex items-center justify-center h-full">
                <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
              </div>
            ) : error ? (
              <div className="flex items-center justify-center h-full text-red-600">
                Error loading invoices
              </div>
            ) : (
              <>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12" aria-label="Select one invoice" />
                      <TableHead>Invoice Date</TableHead>
                      <TableHead>Invoice Ref No</TableHead>
                      <TableHead>GRN No</TableHead>
                      <TableHead className="text-right">Payable Amount</TableHead>
                      <TableHead className="text-right">SGST</TableHead>
                      <TableHead className="text-right">CGST</TableHead>
                      <TableHead className="text-right">IGST</TableHead>
                      <TableHead className="text-right">CESS</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Company</TableHead>
                      <TableHead>Location</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {displayedInvoices.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={12} className="text-center py-8 text-gray-500">
                          No invoices found
                        </TableCell>
                      </TableRow>
                    ) : (
                      displayedInvoices.map((invoice, index) => {
                        return (
                          <TableRow key={invoice.InvRefNo + index}>
                            <TableCell>
                              <Checkbox
                                checked={selectedInvoices.has(invoice.InvRefNo)}
                                onCheckedChange={() => handleCheckboxChange(invoice.InvRefNo)}
                                aria-label={`Select invoice ${invoice.InvRefNo}`}
                              />
                            </TableCell>
                            <TableCell className="whitespace-nowrap">
                              {formatDateDMY(invoice.InvDateGen)}
                            </TableCell>
                            <TableCell className="whitespace-nowrap font-medium">
                              {invoice.InvRefNo || '-'}
                            </TableCell>
                            <TableCell className="whitespace-nowrap">
                              {invoice.GRNNo || '-'}
                            </TableCell>
                            <TableCell className="text-right whitespace-nowrap font-semibold">
                              {formatCurrency(invoice.PayableAmount)}
                            </TableCell>
                            <TableCell className="text-right whitespace-nowrap">
                              {formatCurrency(invoice.SGST)}
                            </TableCell>
                            <TableCell className="text-right whitespace-nowrap">
                              {formatCurrency(invoice.CGST)}
                            </TableCell>
                            <TableCell className="text-right whitespace-nowrap">
                              {formatCurrency(invoice.IGST)}
                            </TableCell>
                            <TableCell className="text-right whitespace-nowrap">
                              {formatCess(invoice.CESS)}
                            </TableCell>
                            <TableCell className="whitespace-nowrap">
                              {formatType(invoice.Type)}
                            </TableCell>
                            <TableCell className="whitespace-nowrap">
                              {invoice.CompanyName || invoice.CompanyID || '-'}
                            </TableCell>
                            <TableCell className="whitespace-nowrap">
                              {invoice.Location || invoice.LocationID || '-'}
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </>
            )}
          </div>

          {/* Mobile Card View */}
          <div className="block lg:hidden flex-1 overflow-y-auto px-4">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="flex flex-col items-center gap-3">
                  <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                  <span className="text-sm text-gray-500">Loading invoices...</span>
                </div>
              </div>
            ) : error ? (
              <div className="text-center py-12 text-red-600">
                Error loading invoices
              </div>
            ) : displayedInvoices.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                No invoices found
              </div>
            ) : (
              <div className="space-y-3 pb-4">
                {displayedInvoices.map((invoice, index) => {
                  return (
                    <Card 
                      key={invoice.InvRefNo + index} 
                      className="bg-white rounded-lg shadow border hover:shadow-md transition-shadow duration-200"
                    >
                      <div className="p-3">
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-start gap-3 flex-1">
                            <Checkbox
                              checked={selectedInvoices.has(invoice.InvRefNo)}
                              onCheckedChange={() => handleCheckboxChange(invoice.InvRefNo)}
                              aria-label={`Select invoice ${invoice.InvRefNo}`}
                              className="mt-0.5"
                            />
                            <div className="font-semibold text-sm text-gray-900">
                              {invoice.InvRefNo || '-'}
                            </div>
                          </div>
                          
                          {/* Three-dot menu */}
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0 hover:bg-gray-100"
                                aria-label="More options"
                              >
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-32">
                              <DropdownMenuItem
                                onClick={() => handleEditInvoice(invoice)}
                                className="cursor-pointer"
                              >
                                <Pencil className="h-4 w-4 mr-2" />
                                Edit
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => handleDeleteInvoiceFromCard(invoice)}
                                className="cursor-pointer text-red-600 focus:text-red-600"
                              >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Delete
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => handleAddQuantities(invoice)}
                                className="cursor-pointer"
                              >
                                <Package className="h-4 w-4 mr-2" />
                                Quantities
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                        
                        <div className="space-y-1.5 text-xs">
                          <div className="flex justify-between items-center py-1">
                            <span className="text-gray-600">Date:</span>
                            <span className="text-gray-900">{formatDateDMY(invoice.InvDateGen)}</span>
                          </div>
                          
                          <div className="flex justify-between items-center py-1">
                            <span className="text-gray-600">GRN No:</span>
                            <span className="text-gray-900">{invoice.GRNNo || '-'}</span>
                          </div>
                          
                          <div className="flex justify-between items-center py-1">
                            <span className="text-gray-600">Amount:</span>
                            <span className="text-gray-900 font-semibold">{formatCurrency(invoice.PayableAmount)}</span>
                          </div>
                          
                          <div className="flex justify-between items-center py-1">
                            <span className="text-gray-600">Type:</span>
                            <span className="text-gray-900">{formatType(invoice.Type)}</span>
                          </div>
                        </div>
                      </div>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      ) : (
        // Payments Section
        <div className="flex-1 flex flex-col overflow-hidden">
          <SupplierPayments 
            token={token} 
            supplierId={supplierId} 
            selectedInvoices={selectedInvoices}
            invoices={displayedInvoices}
            onBack={() => setActiveTab('invoices')}
          />
        </div>
      )}
      <Dialog
        open={isAddInvoiceOpen}
        onOpenChange={(open) => {
          if (!open) {
            handleCloseAddInvoice();
          } else {
            setIsAddInvoiceOpen(true);
          }
        }}
      >
        <DialogContent className="max-w-xl w-[95vw] sm:w-full max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="text-xl font-semibold">Add Supplier Invoice</DialogTitle>
            <DialogDescription>
              Enter the invoice details for the selected supplier. Fields marked with * are required.
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 overflow-y-auto flex-1 min-h-0 pr-2 -mr-2">
            <div className="flex flex-col gap-2">
              <Label htmlFor="invoice-date" className="flex items-center gap-1">
                Date <span className="text-red-500">*</span>
              </Label>
              <Input
                id="invoice-date"
                type="date"
                value={invoiceForm.date}
                onChange={(e) => setInvoiceForm(prev => ({ ...prev, date: e.target.value }))}
                required
                className="border-gray-300"
              />
            </div>
            <div className="flex flex-col gap-2 sm:col-span-1">
              <Label htmlFor="invoice-number" className="flex items-center gap-1">
                Invoice Number (Inv No) <span className="text-red-500">*</span>
              </Label>
              <Input
                id="invoice-number"
                placeholder="INV-2025-001"
                value={invoiceForm.invNo}
                onChange={(e) => setInvoiceForm(prev => ({ ...prev, invNo: e.target.value }))}
                required
                className="border-gray-300"
              />
            </div>
            <div className="flex flex-col gap-2 sm:col-span-2">
              <Label htmlFor="grn-number">
                GRN No
              </Label>
              <Input
                id="grn-number"
                placeholder="123"
                value={invoiceForm.grnNo}
                onChange={(e) => setInvoiceForm(prev => ({ ...prev, grnNo: e.target.value }))}
                className="border-gray-300"
              />
            </div>
            <div className="flex flex-col gap-2 sm:col-span-2">
              <Label>
                Type
              </Label>
              <div className="flex flex-row gap-6 mt-2">
                <div className="flex items-center space-x-2">
                  <input
                    type="radio"
                    id="invoice"
                    name="invoiceType"
                    value="0"
                    checked={invoiceForm.type === '0'}
                    onChange={(e) => {
                      console.log('Invoice radio clicked');
                      setInvoiceForm(prev => ({ ...prev, type: e.target.value }));
                    }}
                    className="h-4 w-4 cursor-pointer"
                  />
                  <Label htmlFor="invoice" className="font-normal cursor-pointer select-none">
                    Invoice
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <input
                    type="radio"
                    id="debit-note"
                    name="invoiceType"
                    value="1"
                    checked={invoiceForm.type === '1'}
                    onChange={(e) => {
                      console.log('Debit Note radio clicked');
                      setInvoiceForm(prev => ({ ...prev, type: e.target.value }));
                    }}
                    className="h-4 w-4 cursor-pointer"
                  />
                  <Label htmlFor="debit-note" className="font-normal cursor-pointer select-none">
                    Debit Note
                  </Label>
                </div>
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <Label className="flex items-center gap-1">
                Company <span className="text-red-500">*</span>
              </Label>
              <Select
                value={invoiceForm.companyId}
                onValueChange={(value) => setInvoiceForm(prev => ({ ...prev, companyId: value }))}
                disabled={isCompanyDisabled}
                required
              >
                <SelectTrigger>
                  <SelectValue placeholder={isCompaniesLoading ? 'Loading companies...' : 'Select company'} />
                </SelectTrigger>
                <SelectContent>
                  {companies.map((company: any) => (
                    <SelectItem key={company.Id || company.CompanyID} value={String(company.Id || company.CompanyID)}>
                      {company.Name || company.CompanyName || `Company ${company.Id || company.CompanyID}`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {!isCompaniesLoading && companies.length === 0 && (
                <p className="text-sm text-red-500">No companies available.</p>
              )}
            </div>
            <div className="flex flex-col gap-2">
              <Label className="flex items-center gap-1">
                Location <span className="text-red-500">*</span>
              </Label>
              <Select
                value={invoiceForm.locationId}
                onValueChange={(value) => setInvoiceForm(prev => ({ ...prev, locationId: value }))}
                disabled={isLocationDisabled}
                required
              >
                <SelectTrigger>
                  <SelectValue placeholder={isLocationsLoading ? 'Loading locations...' : 'Select location'} />
                </SelectTrigger>
                <SelectContent>
                  {locations.map((location: any) => (
                    <SelectItem key={location.ID || location.LocationID || location.LocationId} value={String(location.ID || location.LocationID || location.LocationId)}>
                      {location.Name || location.LocationName || location.Location || `Location ${location.ID || location.LocationID || location.LocationId}`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {!isLocationsLoading && locations.length === 0 && (
                <p className="text-sm text-red-500">No locations available.</p>
              )}
            </div>
            
            {/* Amount and Tax Fields */}
            <div className="flex flex-col gap-2 sm:col-span-2">
              <Label htmlFor="total-amount" className="text-base font-semibold">
                Total Amount
              </Label>
              <Input
                id="total-amount"
                type="number"
                step="0.01"
                placeholder="0.00"
                value={invoiceForm.payableAmount}
                onChange={(e) => setInvoiceForm(prev => ({ ...prev, payableAmount: e.target.value }))}
                className="border-gray-300"
              />
            </div>
            
            <div className="flex flex-col gap-2">
              <Label htmlFor="total-sgst">
                Total SGST
              </Label>
              <Input
                id="total-sgst"
                type="number"
                step="0.01"
                placeholder="0.00"
                value={invoiceForm.sgst}
                onChange={(e) => setInvoiceForm(prev => ({ ...prev, sgst: e.target.value }))}
                className="border-gray-300"
              />
            </div>
            
            <div className="flex flex-col gap-2">
              <Label htmlFor="total-cgst">
                Total CGST
              </Label>
              <Input
                id="total-cgst"
                type="number"
                step="0.01"
                placeholder="0.00"
                value={invoiceForm.cgst}
                onChange={(e) => setInvoiceForm(prev => ({ ...prev, cgst: e.target.value }))}
                className="border-gray-300"
              />
            </div>
            
            <div className="flex flex-col gap-2">
              <Label htmlFor="total-igst">
                Total IGST
              </Label>
              <Input
                id="total-igst"
                type="number"
                step="0.01"
                placeholder="0.00"
                value={invoiceForm.igst}
                onChange={(e) => setInvoiceForm(prev => ({ ...prev, igst: e.target.value }))}
                className="border-gray-300"
              />
            </div>
            
            <div className="flex flex-col gap-2">
              <Label htmlFor="total-cess">
                Total CESS
              </Label>
              <Input
                id="total-cess"
                type="number"
                step="0.01"
                placeholder="0.00"
                value={invoiceForm.cess}
                onChange={(e) => setInvoiceForm(prev => ({ ...prev, cess: e.target.value }))}
                className="border-gray-300"
              />
            </div>
          </div>

          <DialogFooter className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 sm:gap-4 pt-4 flex-shrink-0">
            <Button
              variant="outline"
              onClick={handleCloseAddInvoice}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button onClick={handleSaveInvoice} disabled={isSubmitting}>
              {isSubmitting ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Saving...
                </span>
              ) : (
                'Save'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Update Invoice Dialog */}
      <Dialog
        open={isUpdateInvoiceOpen}
        onOpenChange={(open) => {
          if (!open) {
            handleCloseUpdateInvoice();
          } else {
            setIsUpdateInvoiceOpen(true);
          }
        }}
      >
        <DialogContent className="max-w-md w-[95vw] sm:w-full max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="text-xl font-semibold">Update Supplier Invoice</DialogTitle>
            <DialogDescription>
              Update the invoice details. All fields are required.
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-1 gap-4 overflow-y-auto flex-1 min-h-0 pr-2 -mr-2">
            <div className="flex flex-col gap-2">
              <Label htmlFor="update-invoice-date" className="flex items-center gap-1">
                Date <span className="text-red-500">*</span>
              </Label>
              <Input
                id="update-invoice-date"
                type="date"
                value={updateForm.date}
                onChange={(e) => setUpdateForm(prev => ({ ...prev, date: e.target.value }))}
                required
                className="border-gray-300"
              />
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="update-invoice-number" className="flex items-center gap-1">
                Invoice Number (Inv No) <span className="text-red-500">*</span>
              </Label>
              <Input
                id="update-invoice-number"
                placeholder="INV-2025-001"
                value={updateForm.invNo}
                onChange={(e) => setUpdateForm(prev => ({ ...prev, invNo: e.target.value }))}
                required
                className="border-gray-300"
              />
            </div>
          </div>

          <DialogFooter className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 sm:gap-4 pt-4 flex-shrink-0">
            <Button
              variant="outline"
              onClick={handleCloseUpdateInvoice}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button onClick={handleUpdateInvoice} disabled={isSubmitting}>
              {isSubmitting ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Updating...
                </span>
              ) : (
                'Update'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={isDeleteConfirmOpen}
        onOpenChange={(open) => {
          if (!open) {
            handleCloseDeleteConfirm();
          } else {
            setIsDeleteConfirmOpen(true);
          }
        }}
      >
        <DialogContent className="max-w-md w-[95vw] sm:w-full">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold text-red-600">Delete Supplier Invoice</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete {selectedInvoices.size} {selectedInvoices.size === 1 ? 'invoice' : 'invoices'}? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>

          <DialogFooter className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 sm:gap-4 pt-4">
            <Button
              variant="outline"
              onClick={handleCloseDeleteConfirm}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDeleteInvoice} 
              disabled={isDeleting}
            >
              {isDeleting ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Deleting...
                </span>
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-1" />
                  Delete
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <DebitNotePreview
        open={isDebitNotePreviewOpen}
        onClose={handleCloseDebitNotePreview}
        data={debitNotePreviewData}
        isLoading={isLoadingDebitNote}
        error={debitNoteError}
      />
    </div>
  );
};

export default SupplierInvoicesPayments;

